import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Users, IndianRupee, MapPin, Calendar, FileText, CheckCircle, Target } from 'lucide-react';

const ExploreProjectsPage = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    orgName: '',
    contactName: '',
    email: '',
    phone: '',
    title: '',
    sector: '',
    state: '',
    fundingMin: '',
    fundingMax: '',
    timeline: '',
    expectedBeneficiaries: '',
    summary: ''
  });

  const sectorOptions = [
    'Education',
    'Healthcare',
    'Skill Development',
    'Women Empowerment',
    'Rural Development',
    'Environment & Sustainability',
    'Sanitation',
    'Sports',
    'Armed Forces Veterans'
  ];

  const stateOptions = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/xchange/projects/submit-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          fundingMin: parseFloat(formData.fundingMin),
          fundingMax: parseFloat(formData.fundingMax),
          expectedBeneficiaries: parseInt(formData.expectedBeneficiaries) || 0
        })
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        const error = await response.json();
        alert(error.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-28 pb-16 px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-lg mx-auto mb-6 flex items-center justify-center bg-muted">
              <CheckCircle className="w-8 h-8 text-foreground" />
            </div>
            <h1 className="heading-1 text-foreground mb-4">
              Project Submitted Successfully!
            </h1>
            <p className="body-large text-muted-foreground mb-8">
              Your project proposal has been received and is now <strong className="text-foreground">pending verification</strong>. Our team will review it within 24-48 hours.
            </p>
            <div className="card-base p-6 mb-8 text-left">
              <h3 className="heading-4 text-foreground mb-4">What Happens Next?</h3>
              <ul className="space-y-3 text-foreground">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>1</span>
                  <span><strong>Acknowledgment Email:</strong> You'll receive a confirmation email shortly</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>2</span>
                  <span><strong>Admin Verification:</strong> TMG team reviews and verifies your project details (24-48 hours)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>3</span>
                  <span><strong>Pending Listing:</strong> Your project appears on Xchange with "Pending Verification" tag</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>4</span>
                  <span><strong>Approval Email:</strong> Once verified, you'll be notified and full listing goes live</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>5</span>
                  <span><strong>Funding Opportunities:</strong> TMG connects you with relevant CSR sponsors</span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/start-funding')} className="btn-primary btn-lg">View CSR Sponsors</button>
              <button onClick={() => navigate('/')} className="btn-outline btn-lg">Back to Home</button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4 md:px-8 gradient-warm relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="heading-1 text-foreground mb-4">List Your CSR Project</h1>
          <p className="body-large text-muted-foreground max-w-3xl mx-auto">
            Get discovered by corporate CSR sponsors and secure funding for your social impact initiatives
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="card-base p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* Organization Details */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <Users className="w-6 h-6" />
                  Organization Details
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label mb-1.5 block">
                      Organization Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.orgName}
                      onChange={(e) => setFormData({...formData, orgName: e.target.value})}
                      className="input-base"
                      placeholder="e.g., Women Empowerment Foundation"
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.contactName}
                      onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                      className="input-base"
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-base"
                      placeholder="contact@ngo.org"
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-base"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <Target className="w-6 h-6" />
                  Project Details
                </h3>
                <div className="space-y-6">
                  <div>
                    <label className="label mb-1.5 block">
                      Project Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="input-base"
                      placeholder="e.g., Women's Livelihood Program - Rajasthan"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="label mb-1.5 block">
                        Primary Sector <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.sector}
                        onChange={(e) => setFormData({...formData, sector: e.target.value})}
                        className="input-base"
                      >
                        <option value="">Select Sector</option>
                        {sectorOptions.map((sector) => (
                          <option key={sector} value={sector}>{sector}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label mb-1.5 block">
                        State <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        className="input-base"
                      >
                        <option value="">Select State</option>
                        {stateOptions.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="label mb-1.5 block">
                      Project Summary <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      value={formData.summary}
                      onChange={(e) => setFormData({...formData, summary: e.target.value})}
                      className="input-base resize-none"
                      rows="4"
                      maxLength="600"
                      placeholder="Describe your project, its objectives, and expected impact (max 600 characters)"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{formData.summary.length}/600 characters</p>
                  </div>
                </div>
              </div>

              {/* Funding & Timeline */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <IndianRupee className="w-6 h-6" />
                  Funding Requirements
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label mb-1.5 block">
                      Minimum Funding (₹ Lakhs) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.1"
                      value={formData.fundingMin}
                      onChange={(e) => setFormData({...formData, fundingMin: e.target.value})}
                      className="input-base"
                      placeholder="e.g., 25"
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Maximum Funding (₹ Lakhs) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.1"
                      value={formData.fundingMax}
                      onChange={(e) => setFormData({...formData, fundingMax: e.target.value})}
                      className="input-base"
                      placeholder="e.g., 100"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <Calendar className="w-6 h-6" />
                  Additional Details
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label mb-1.5 block">
                      Project Timeline <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.timeline}
                      onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                      className="input-base"
                    >
                      <option value="">Select Timeline</option>
                      <option value="3-6 months">3-6 months</option>
                      <option value="6-12 months">6-12 months</option>
                      <option value="1-2 years">1-2 years</option>
                      <option value="2+ years">2+ years</option>
                    </select>
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Expected Beneficiaries
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.expectedBeneficiaries}
                      onChange={(e) => setFormData({...formData, expectedBeneficiaries: e.target.value})}
                      className="input-base"
                      placeholder="e.g., 1000"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button type="submit" className="btn-primary flex-1 btn-lg">Submit Project Proposal</button>
                <button type="button" onClick={() => navigate('/')} className="btn-outline btn-lg">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExploreProjectsPage;
